/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage } from "@i18n/next";
import type { CommunityActivityFeedResultPromptResponse } from "@packages/sdk";
import {
  communityActivityFeedReferenceTypeEnum,
  getFullName,
  useDate,
} from "@packages/sdk";
import type { MaskProps, ThemeType } from "@packages/ui";
import {
  Avatar,
  colors,
  constantSemanticColors,
  radius,
  ScrollArea,
  spacing,
  Text,
} from "@packages/ui";
import Image from "next/image";
import type { HTMLAttributes } from "react";
import { forwardRef } from "react";

import { RemovePostDropdownMenu } from "../../../RemovePostDropdownMenu";

const baseStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.ms,
};

const cardStyles: Interpolation = {
  height: spacing.full,
  left: spacing.none,
  position: "absolute",
  top: spacing.none,
  width: spacing.full,
};

const cardContainerStyles: Interpolation = {
  paddingBottom: spacing.full,
  position: "relative",
  width: spacing.full,
};

const contentContainerStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.ms,
};

const contentHeaderStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
  width: spacing.full,
};

const contentHeaderImageStyles: Interpolation = {
  borderRadius: radius.xs,
};

const contentHeaderImageContainerStyles: Interpolation = {
  height: spacing.l,
  minHeight: spacing.l,
  minWidth: spacing.l,
  position: "relative",
  width: spacing.l,
};

const contentHeaderTitleStyles: Interpolation = {
  color: colors.white,
};

const headerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
};

const headerTextStyles: Interpolation = {
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: spacing.t,
  minWidth: spacing.t,
};

const headerTextTimeStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsMedium,
});

const scrollAreaStyles: Interpolation = {
  height: spacing.full,
};

const scrollAreaViewportStyles = (props: {
  backgroundColor: string;
}): Interpolation => ({
  backgroundColor: props.backgroundColor,
  borderRadius: spacing.m,
  display: "flex",
  flexDirection: "column",
  padding: spacing.xl,
});

const scrollAreaMaskBottomStyles: Interpolation = {
  borderBottomLeftRadius: spacing.m,
  borderBottomRightRadius: spacing.m,
};

const scrollAreaMaskTopStyles: Interpolation = {
  borderTopLeftRadius: spacing.m,
  borderTopRightRadius: spacing.m,
};

const textStyles: Interpolation = {
  color: colors.white,
};

const textPromptStyles: Interpolation = {
  color: constantSemanticColors.onColorVeryHigh,
  flex: 1,
};

type ActivityCardPromptResponseProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> & {
  data: CommunityActivityFeedResultPromptResponse;
};

export const ActivityCardPromptResponse = forwardRef<
  HTMLDivElement,
  ActivityCardPromptResponseProps
>(({ data, ...props }, ref) => {
  const { getDateFormatDifferenceNow } = useDate();

  return (
    <div ref={ref} css={baseStyles} {...props}>
      <div css={headerStyles}>
        <Avatar imgSrc={data.user.image_url} size={40} />
        <div css={headerTextStyles}>
          <Text overflow="ellipsis" size="xl" type="detail">
            <strong>
              {getFullName({
                firstName: data?.user.name,
                lastName: data?.user.last_name,
              })}
            </strong>
            &nbsp;
            <I18nMessage message="responded_to_a_discussion" />
          </Text>
          <Text size="l" css={headerTextTimeStyles} type="detail">
            {getDateFormatDifferenceNow({ date: new Date(data.created_at) })}
          </Text>
        </div>
        <RemovePostDropdownMenu
          communityPostId={data.id}
          type={communityActivityFeedReferenceTypeEnum.enum.prompt_response}
        />
      </div>
      <div css={cardContainerStyles}>
        <div css={cardStyles}>
          <ScrollArea
            maskBottom
            maskBottomProps={{
              color: data.reference.prayer?.images
                ?.color_hex as MaskProps["color"],
            }}
            maskBottomCSS={[scrollAreaMaskBottomStyles]}
            maskTop
            maskTopProps={{
              color: data.reference.prayer?.images
                ?.color_hex as MaskProps["color"],
            }}
            maskTopCSS={[scrollAreaMaskTopStyles]}
            css={scrollAreaStyles}
            tableMarginAuto
            viewportCSS={[
              scrollAreaViewportStyles({
                backgroundColor: data.reference.prayer?.images?.color_hex,
              }),
            ]}
          >
            <div css={contentContainerStyles}>
              <div css={contentHeaderStyles}>
                <div css={contentHeaderImageContainerStyles}>
                  <Image
                    alt={data.reference.prayer.title}
                    fill
                    objectFit="cover"
                    objectPosition="center"
                    src={data.reference.prayer.images.fallback}
                    css={contentHeaderImageStyles}
                  />
                </div>
                <Text
                  as="h3"
                  size="xs"
                  css={contentHeaderTitleStyles}
                  type="title"
                >
                  {data.reference.prayer.title}
                </Text>
              </div>
              <Text as="span" size="l" css={textPromptStyles} type="quote">
                {data.reference.prayer.prompt}
              </Text>
              <Text as="p" size="l" css={textStyles} type="body">
                {data.reference.text}
              </Text>
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
});

ActivityCardPromptResponse.displayName = "ActivityCardPromptResponse";
